/**
 * The chat drawer's slide, from the shell's side: how long the drawer stays
 * mounted, which pose it animates toward, and whether the slide is running.
 *
 * One owner for all three, and for the box they act on. The drawer is the
 * element that transitions, so it reports when the slide really starts and
 * stops; everything those reports drive — the stage-animation window, the
 * live-follow flag, the fallback for a slide that never runs — is decided
 * here, so no deadline is computed in two places.
 */

import type { RefObject } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { beginStageAnimation } from '../stageMotion';

import { CHAT_SLIDE_WINDOW_MS } from './slideMotion';

// Margin past the slide window before the fallback ends a slide that never got a
// transition, so it does not race a real transitionend.
const SLIDE_FALLBACK_MARGIN_MS = 300;

export function useChatSlide(isChatOpen: boolean): {
    isMounted: boolean;
    isShown: boolean;
    isSliding: boolean;
    wrapRef: RefObject<HTMLDivElement | null>;
    onSlideMotion: (isMoving: boolean) => void;
} {
    // The drawer slides rather than appearing, which splits "is it open?" into
    // two flags. `isMounted` covers the whole visible lifetime — it turns on with
    // the open and lingers past the close so the exit can play; `isShown` is the
    // pose the drawer animates toward, and trails the mount by one forced style
    // resolution so the width transition has a from-value to ease out of
    // (mounting straight into the open pose would just snap). Both start from
    // `isChatOpen`, so a page that boots with the chat open paints it in place
    // rather than sliding it in at load.
    const [isMounted, setMounted] = useState(isChatOpen);
    const [isShown, setShown] = useState(isChatOpen);
    // True while the drawer's width transition is actually running. The slide is
    // the ONLY animation — panels have no transition of their own toward the
    // new layout; they re-allocate for the live stage width on every frame the
    // drawer takes (useStageLayout), exactly like a splitter drag. This flag
    // routes the slide through the same live path (data-resizing suppresses
    // panel-edge easing; PanelStage skips per-frame animation bookkeeping), so
    // drawer, rail and every panel all derive from one animated value and
    // cannot fall out of step, no matter how late or slow frames arrive.
    const [isSliding, setSliding] = useState(false);
    // Fallback for a slide whose transition never runs (reduced motion, a
    // hidden tab): cleared the moment the real transition reports in.
    const fallbackRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const wrapRef = useRef<HTMLDivElement>(null);

    // The freeze matters: panel bodies get their own compositor layer for the
    // duration (syncPanelLayer), which is what stops the stage re-recording the
    // display list of a panel that only slides on every animated frame.
    // Profiled against a loaded Objects gallery, a toggle without this repaints
    // every visible thumbnail ~14 times over.
    //
    // Anchored on every start, so the drawer's transitionstart — the frame the
    // slide REALLY begins, which a heavy first paint can push well past the
    // toggle — extends the window it was opened with below.
    const onSlideMotion = useCallback((isMoving: boolean): void => {
        if (isMoving) {
            clearTimeout(fallbackRef.current);
            fallbackRef.current = undefined;
            beginStageAnimation(CHAT_SLIDE_WINDOW_MS, { panelTransition: true });
        }
        setSliding(isMoving);
    }, []);

    // Mounted collapsed during THIS render rather than from an effect. React
    // re-runs the render immediately and commits once, so the drawer is in the
    // DOM by the time layout effects run and can be posed in the same commit.
    if (isChatOpen && !isMounted) {
        setMounted(true);
    }

    const firstEffect = useRef(true);
    useLayoutEffect((): (() => void) | undefined => {
        // Initial state already matches isChatOpen; there is no slide or timer work.
        if (firstEffect.current) {
            firstEffect.current = false;
            return undefined;
        }

        let unmountTimer: ReturnType<typeof setTimeout> | undefined;

        // Enter the live-follow posture before the first animated frame; the
        // drawer's own transitionstart re-anchors it at the true start, and the
        // timer here only covers a slide that never got a transition at all.
        onSlideMotion(true);
        fallbackRef.current = setTimeout(
            (): void => setSliding(false), CHAT_SLIDE_WINDOW_MS + SLIDE_FALLBACK_MARGIN_MS);

        if (isChatOpen) {
            // The drawer is already in the DOM collapsed (mounted during render), so
            // this read resolves its 0-width style — the from-value the transition
            // eases out of — and the pose can be queued right here.
            wrapRef.current?.getBoundingClientRect();
            setShown(true);
        } else {
            setShown(false);
            unmountTimer = setTimeout((): void => setMounted(false), CHAT_SLIDE_WINDOW_MS);
        }

        // Re-toggling cancels the previous deadlines before starting fresh; this
        // also prevents a pending state update after AppShell unmounts.
        return (): void => {
            clearTimeout(unmountTimer);
            clearTimeout(fallbackRef.current);
            fallbackRef.current = undefined;
        };
    }, [isChatOpen, onSlideMotion]);

    return { isMounted, isShown, isSliding, wrapRef, onSlideMotion };
}
